import React from 'react';
import PropTypes from 'prop-types';
import {
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { ReferenceField, TextField, useTranslate } from 'react-admin';

import { GridCard } from '../../../components';

const useStyles = makeStyles((theme) => {
  return {
    empty: {
      fontStyle: 'italic',
      color: theme.palette.text.disabled,
      padding: theme.spacing(2),
    },
    message: {
      padding: theme.spacing(2),
    },
    error: {
      padding: theme.spacing(2),
      color: theme.palette.error.main,
    },
    table: {
      width: '100%',
    },
    tableContainer: {
      width: '100%',
      overflowX: 'auto',
    },
    headerCell: {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.palette.text.secondary,
    },
    inProgressChip: {
      fontWeight: 600,
    },
    dash: {
      color: theme.palette.text.disabled,
    },
  };
});

/**
 * Format YYYY-MM-DD → DD/MM/YYYY (locale fr).
 */
const formatDate = (iso) => {
  if (!iso) {
    return null;
  }
  const parts = iso.split('-');
  if (parts.length !== 3) {
    return iso;
  }
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

/**
 * Tri par date de début DESC. Les tenures sans start (edge case) passent
 * en dernier.
 */
const sortTenuresDesc = (tenures) => {
  return [...tenures].sort((a, b) => {
    if (!a.start && !b.start) {
      return 0;
    }
    if (!a.start) {
      return 1;
    }
    if (!b.start) {
      return -1;
    }
    return b.start.localeCompare(a.start);
  });
};

const TenureRow = ({ tenure, rowKey }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const start = formatDate(tenure.start);
  const end = formatDate(tenure.end);

  return (
    <TableRow key={rowKey}>
      <TableCell>
        {translate(`resources.account_operational_info.role.${tenure.role}`, {
          _: tenure.role,
        })}
      </TableCell>
      <TableCell>
        {translate(
          `resources.account_operational_info.position.${tenure.position}`,
          { _: tenure.position },
        )}
      </TableCell>
      <TableCell>
        {tenure.tenant ? (
          <ReferenceField
            record={tenure}
            source="tenant"
            reference="cleaner"
            basePath="/cleaner"
            link="show"
            allowEmpty
          >
            <TextField source="fullname" />
          </ReferenceField>
        ) : (
          <span className={classes.dash}>—</span>
        )}
      </TableCell>
      <TableCell>{start || <span className={classes.dash}>—</span>}</TableCell>
      <TableCell>
        {end || (
          <Chip
            label={translate(
              'resources.account_operational_info.tenure.in_progress',
            )}
            color="primary"
            size="small"
            className={classes.inProgressChip}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

TenureRow.propTypes = {
  tenure: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
    tenant: PropTypes.string,
    role: PropTypes.string,
    position: PropTypes.string,
  }).isRequired,
  rowKey: PropTypes.string.isRequired,
};

const TenureHistoryCard = ({ record, isLoading, error, ...props }) => {
  const classes = useStyles();
  const translate = useTranslate();

  const renderBody = () => {
    if (isLoading) {
      return (
        <Typography className={classes.message}>
          {translate('ra.page.loading')}
        </Typography>
      );
    }
    if (error) {
      return (
        <Typography className={classes.error}>
          {translate('resources.account_operational_info.error', {
            _: 'Erreur lors du chargement',
          })}{' '}
          : {error.message}
        </Typography>
      );
    }
    if (!record) {
      return (
        <Typography className={classes.empty}>
          {translate('resources.account_operational_info.empty')}
        </Typography>
      );
    }

    const tenures =
      (record && record.metadata && record.metadata.tenures) || [];

    if (tenures.length === 0) {
      return (
        <Typography className={classes.empty}>
          {translate('resources.account_operational_info.no_tenures')}
        </Typography>
      );
    }

    const sorted = sortTenuresDesc(tenures);

    return (
      <Grid item xs={12}>
        <Paper className={classes.tableContainer} elevation={0}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>
                  {translate(
                    'resources.account_operational_info.fields.tenure_role',
                  )}
                </TableCell>
                <TableCell className={classes.headerCell}>
                  {translate(
                    'resources.account_operational_info.fields.tenure_position',
                  )}
                </TableCell>
                <TableCell className={classes.headerCell}>
                  {translate(
                    'resources.account_operational_info.fields.tenure_tenant',
                  )}
                </TableCell>
                <TableCell className={classes.headerCell}>
                  {translate(
                    'resources.account_operational_info.fields.tenure_start',
                  )}
                </TableCell>
                <TableCell className={classes.headerCell}>
                  {translate(
                    'resources.account_operational_info.fields.tenure_end',
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((tenure, index) => {
                const rowKey = `${tenure.role}-${
                  tenure.position
                }-${tenure.start || 'nostart'}-${index}`;
                return (
                  <TenureRow key={rowKey} tenure={tenure} rowKey={rowKey} />
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    );
  };

  return (
    <GridCard
      {...props}
      title={translate('resources.account.card_title.tenures')}
      xs={12}
    >
      {renderBody()}
    </GridCard>
  );
};

TenureHistoryCard.propTypes = {
  record: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.instanceOf(Error),
};

TenureHistoryCard.defaultProps = {
  record: null,
  isLoading: false,
  error: null,
};

export default TenureHistoryCard;
